import { createPlaidLinkToken } from "@/api/bank";

export type PlaidLinkChannel = "bank" | "venmo";

export type PlaidOnSuccessMetadata = {
  accounts?: Array<{ id: string; name?: string; mask?: string }>;
  institution?: { name?: string; institution_id?: string };
  [key: string]: unknown;
};

type PlaidConfig = {
  channel: PlaidLinkChannel;
  onSuccess: (publicToken: string, metadata: PlaidOnSuccessMetadata) => void | Promise<void>;
  onExit?: () => void;
};

type PlaidHandler = {
  open: () => void;
};

declare global {
  interface Window {
    Plaid?: {
      create: (config: { token: string; onSuccess: PlaidConfig["onSuccess"]; onExit?: PlaidConfig["onExit"] }) => PlaidHandler;
    };
  }
}

const SCRIPT_URL = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
let plaidPromise: Promise<typeof window.Plaid> | null = null;

export async function loadPlaid(): Promise<typeof window.Plaid> {
  if (window.Plaid) {
    return window.Plaid;
  }
  if (!plaidPromise) {
    plaidPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.Plaid);
      script.onerror = () => reject(new Error("Unable to load Plaid Link"));
      document.body.appendChild(script);
    });
  }
  return plaidPromise;
}

export async function openPlaidLink({ channel, onSuccess, onExit }: PlaidConfig): Promise<void> {
  const { link_token } = await createPlaidLinkToken({ channel });
  const plaid = await loadPlaid();
  if (!plaid) {
    throw new Error("Plaid Link is unavailable");
  }
  const handler = plaid.create({
    token: link_token,
    onSuccess,
    onExit,
  });
  handler.open();
}
